/* eslint-disable */
// useAccessControlData — the shared reads for the Access Control center.
//   users        ← user_profiles (display_name, email, role, is_super_admin, …)
//   onlineEmails ← user_sessions with last_seen_at within 5 min (Q7 — same gate
//                  UserManagement uses)
//   permissions  ← user_portal_permissions (user_id, portal_id, role) — the matrix
// Realtime invalidation on permission changes (mirrors PermissionsPanel). Admin-gated
// at the mount site (super-admin only); RLS additionally enforces admin-only writes.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { supabase } from '../../../../supabase';

const FIVE_MIN = 5 * 60 * 1000;

export function useAccessControlData() {
  const qc = useQueryClient();

  const usersQ = useQuery({
    queryKey: ['ac', 'users'],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.from('user_profiles').select('*');
      if (error) throw error;
      return data || [];
    },
  });

  const onlineQ = useQuery({
    queryKey: ['ac', 'online'],
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async () => {
      const since = new Date(Date.now() - FIVE_MIN).toISOString();
      const { data, error } = await supabase
        .from('user_sessions')
        .select('email, last_seen_at')
        .gte('last_seen_at', since);
      if (error) throw error;
      return data || [];
    },
  });

  const permsQ = useQuery({
    queryKey: ['ac', 'permissions'],
    staleTime: 15 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.from('user_portal_permissions').select('user_id, portal_id, role, qc_role');
      if (error) throw error;
      return data || [];
    },
  });

  // Realtime — keep the matrix fresh across admins (mirrors PermissionsPanel).
  useEffect(() => {
    const ch = supabase.channel('ac-perm-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_portal_permissions' }, () => {
        qc.invalidateQueries({ queryKey: ['ac', 'permissions'] });
        qc.invalidateQueries({ queryKey: ['portal-permissions'] });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [qc]);

  const onlineEmails = useMemo(
    () => new Set((onlineQ.data || []).map((r) => (r.email || '').toLowerCase())),
    [onlineQ.data]
  );

  return {
    users: usersQ.data || [],
    loading: usersQ.isLoading || permsQ.isLoading,
    onlineEmails,
    permissions: permsQ.data || [],
    refetchUsers: usersQ.refetch,
  };
}
